import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2,
  BadgeAlert,
  UserPlus,
  Pencil,
  X,
  PlusCircle,
  Map as MapIcon,
  Play,
  ExternalLink,
} from 'lucide-react';
import { readScope, writeScope, Area, SME } from '../utils/scope';
import { useEngagement } from '../hooks/useEngagement';
import { useSession } from '../hooks/useSession';
import { deriveStreamMappingStatus } from '../utils/streamVsmModel';
import '../styles/ScopeWidget.css';

// 023-programme-cockpit (Cycle 2, T017) — "Candidate value streams" scope widget.
// Canonical mock: Mockups/ArcPilot Lite Dashboard Cockpit.html (the "Candidate
// value streams · Setup 3 of 3" card).
//
// The leadership-level scope unit is a candidate value stream (never "domain").
// An area carries an optional one-liner and assigned SMEs; an area with >= 1
// SME is the unlock-bearing unit for the Frame gate. This widget reads and
// writes the persisted scope via utils/scope.ts (readScope/writeScope under
// "arcpilot.scope") — localStorage is the only seam (no hook). It mirrors the
// OrganisationContext idioms: real inputs with accessible names, inline
// focus-managed editors, lucide-only icons, tokens-only CSS. Coral is NEVER
// spent here — the scope step is a warm-up, not the one urgent move.

export type ScopeWidgetProps = {
  /** Called after every persist so the host can re-derive the dashboard. */
  onChange?: () => void;
};

type StreamArea = Area & {
  mappingSessionId?: string;
  vsmBaseline?: unknown;
};

let smeIdSeq = 0;
function freshAreaId(): string {
  smeIdSeq += 1;
  return `area-${smeIdSeq}-${Math.abs(hashString(String(smeIdSeq)))}`;
}

// A tiny deterministic hash so stable ids do not lean on Date.now()/random
// (keeps the widget testable and the ids unique enough within a session).
function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return h;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// ── Inline "assign SME" popover (real inputs, focus-managed) ─────────────────

type AssignSmeFormProps = {
  onSave: (sme: SME) => void;
  onCancel: () => void;
};

const AssignSmeForm: React.FC<AssignSmeFormProps> = ({ onSave, onCancel }) => {
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const nameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const submit = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed) return; // a blank SME name attaches nothing
    const sme: SME = { name: trimmed };
    const c = contact.trim();
    if (c) sme.contact = c;
    onSave(sme);
  }, [name, contact, onSave]);

  return (
    <div className="sw-assign" role="group" aria-label="Assign an SME">
      <input
        ref={nameRef}
        className="sw-field"
        type="text"
        aria-label="SME name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        placeholder="Who knows this stream? — e.g. Jamie Oke"
      />
      <input
        className="sw-field"
        type="text"
        aria-label="Contact or role"
        value={contact}
        onChange={(e) => setContact(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        placeholder="Email or role (optional)"
      />
      <div className="sw-assign-actions">
        <button type="button" className="sw-primary-sm" onClick={submit}>
          Save SME
        </button>
        <button type="button" className="sw-link-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
};

// ── Inline "rename area" editor (real input, focus-managed) ──────────────────

type EditAreaFormProps = {
  initialName: string;
  onSave: (name: string) => void;
  onCancel: () => void;
};

const EditAreaForm: React.FC<EditAreaFormProps> = ({ initialName, onSave, onCancel }) => {
  const [name, setName] = useState(initialName);
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const submit = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed);
  }, [name, onSave]);
  return (
    <div className="sw-editarea">
      <input
        ref={ref}
        className="sw-field"
        type="text"
        aria-label="Area name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
      />
      <button type="button" className="sw-primary-sm" onClick={submit}>
        Save
      </button>
      <button type="button" className="sw-link-btn" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
};

// ── One area row ─────────────────────────────────────────────────────────────

type AreaRowProps = {
  area: StreamArea;
  isAssigning: boolean;
  isEditing: boolean;
  isMapping: boolean;
  mappingError: string | null;
  onAssignOpen: () => void;
  onAssignSave: (sme: SME) => void;
  onAssignCancel: () => void;
  onEditOpen: () => void;
  onEditSave: (name: string) => void;
  onEditCancel: () => void;
  onRemove: () => void;
  onMap: () => void;
};

const AreaRow: React.FC<AreaRowProps> = ({
  area,
  isAssigning,
  isEditing,
  isMapping,
  mappingError,
  onAssignOpen,
  onAssignSave,
  onAssignCancel,
  onEditOpen,
  onEditSave,
  onEditCancel,
  onRemove,
  onMap,
}) => {
  const sme = area.smes && area.smes.length >= 1 ? area.smes[0] : null;
  const needsSme = !sme;
  const status = deriveStreamMappingStatus(area);
  const isBaselineCaptured = status.primaryAction === 'open-vsm';

  return (
    <div className={`sw-row${needsSme ? ' sw-row--needs' : ''}`} data-testid="area-row">
      <Building2
        size={18}
        className={`sw-row-icon${needsSme ? ' sw-row-icon--needs' : ''}`}
        aria-hidden="true"
      />
      <div className="sw-row-body">
        {isEditing ? (
          <EditAreaForm initialName={area.name} onSave={onEditSave} onCancel={onEditCancel} />
        ) : (
          <>
            <div className="sw-row-name">{area.name}</div>
            {area.oneLiner ? <div className="sw-row-oneliner">{area.oneLiner}</div> : null}
            {needsSme ? (
              <div className="sw-row-blocker">
                <BadgeAlert size={13} aria-hidden="true" /> Needs an SME before it can be mapped
              </div>
            ) : null}
            <div className={`sw-map-status sw-map-status--${status.state}`}>
              <MapIcon size={13} aria-hidden="true" />
              <span>{status.label}</span>
            </div>
            {mappingError ? (
              <div className="sw-map-error" role="alert">
                {mappingError}
              </div>
            ) : null}
          </>
        )}
      </div>

      {!isEditing && area.mappingSessionId ? (
        <Link
          className="sw-map-link"
          to={`/streams/${encodeURIComponent(area.id)}/workspace?view=vsm`}
          aria-label={`Open VSM workspace for ${area.name}`}
        >
          <ExternalLink size={14} aria-hidden="true" />
          Open VSM
        </Link>
      ) : null}

      {!isEditing && !isBaselineCaptured ? (
        <button
          type="button"
          className="sw-map-btn"
          aria-label={`${area.mappingSessionId ? 'Resume' : 'Start'} mapping ${area.name}`}
          onClick={onMap}
          disabled={isMapping}
        >
          <Play size={14} aria-hidden="true" />
          {isMapping ? 'Opening...' : area.mappingSessionId ? 'Resume mapping' : 'Start mapping'}
        </button>
      ) : null}

      {!isEditing && sme ? (
        <span className="sw-smechip">
          <span className="sw-smeavatar" aria-hidden="true">
            {initials(sme.name)}
          </span>
          {sme.name}
        </span>
      ) : null}

      {!isEditing && needsSme && !isAssigning ? (
        <button
          type="button"
          className="sw-assign-btn"
          aria-label={`Assign SME to ${area.name}`}
          onClick={onAssignOpen}
        >
          <UserPlus size={15} aria-hidden="true" /> Assign SME
        </button>
      ) : null}

      {!isEditing ? (
        <button
          type="button"
          className="sw-rowact"
          aria-label={`Edit value stream ${area.name}`}
          title="Edit value stream"
          onClick={onEditOpen}
        >
          <Pencil size={15} aria-hidden="true" />
        </button>
      ) : null}

      {!isEditing ? (
        <button
          type="button"
          className="sw-rowact"
          aria-label={`Remove value stream ${area.name}`}
          title="Remove value stream"
          onClick={onRemove}
        >
          <X size={15} aria-hidden="true" />
        </button>
      ) : null}

      {isAssigning && !isEditing ? (
        <AssignSmeForm onSave={onAssignSave} onCancel={onAssignCancel} />
      ) : null}
    </div>
  );
};

// ── The widget ───────────────────────────────────────────────────────────────

const ScopeWidget: React.FC<ScopeWidgetProps> = ({ onChange }) => {
  const [areas, setAreas] = useState<StreamArea[]>(() => readScope().areas as StreamArea[]);
  const [draftName, setDraftName] = useState('');
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [mappingId, setMappingId] = useState<string | null>(null);
  const [mappingErrors, setMappingErrors] = useState<Record<string, string>>({});
  const addRef = useRef<HTMLInputElement | null>(null);

  // 027 B2 — the engagement aggregate is the home of the `streams` sub-doc.
  // When an engagement is resolved we persist through the provider's saveStreams
  // (the INVERSE of readScope's read adapter: the in-widget { areas } state maps
  // back to the { streams: [...] } sub-doc — same { id, name, oneLiner?, smes }
  // field set, only the top-level key flips). With NO engagement resolved (no
  // provider / first-run) we fall back to the legacy localStorage { areas } home.
  const { currentEngagementId, snapshot, saveStreams } = useEngagement();

  useEffect(() => {
    addRef.current?.focus();
  }, []);

  // 027 B2 — when an engagement is resolved, the displayed areas come straight
  // from THAT engagement's snapshot (read DURING render, mapped from its `streams`
  // sub-doc) so they appear in the SAME render the snapshot resolves in — no
  // effect round-trip, no leak across a switch (the snapshot is the source of
  // truth). The local `areas` state remains the editing buffer: persist() updates
  // it optimistically and (for a resolved engagement) saveStreams republishes the
  // snapshot so the next render reflects the save. With NO engagement resolved the
  // local state seeded from legacy localStorage stands.
  const displayAreas = (currentEngagementId && snapshot ? readScope().areas : areas) as StreamArea[];

  // The mutation handlers always derive from the CURRENT truth (displayAreas):
  // the resolved engagement's snapshot when present, else the local buffer. A ref
  // keeps that current value addressable from the stable useCallback handlers.
  const baseAreasRef = useRef<StreamArea[]>(displayAreas);
  baseAreasRef.current = displayAreas;

  // Persist + lift the new area list, then notify the host so it can re-derive.
  const persist = useCallback(
    (next: StreamArea[]) => {
      setAreas(next);
      if (currentEngagementId) {
        // Inverse of readScope's { streams } → { areas } adapter.
        void saveStreams({ streams: next });
      } else {
        writeScope({ areas: next });
      }
      if (onChange) onChange();
    },
    [onChange, currentEngagementId, saveStreams],
  );

  const commitAdd = useCallback(() => {
    const name = draftName.trim();
    if (!name) {
      setDraftName('');
      return; // blank / whitespace adds nothing (no fabricated default)
    }
    const next: Area = { id: freshAreaId(), name, smes: [] };
    persist([...baseAreasRef.current, next]);
    setDraftName('');
  }, [draftName, persist]);

  const onAssignSave = useCallback(
    (id: string, sme: SME) => {
      persist(baseAreasRef.current.map((a) => (a.id === id ? { ...a, smes: [...a.smes, sme] } : a)));
      setAssigningId(null);
    },
    [persist],
  );

  const onEditSave = useCallback(
    (id: string, name: string) => {
      persist(baseAreasRef.current.map((a) => (a.id === id ? { ...a, name } : a)));
      setEditingId(null);
    },
    [persist],
  );

  const onRemove = useCallback(
    (id: string) => {
      setAssigningId((cur) => (cur === id ? null : cur));
      setEditingId((cur) => (cur === id ? null : cur));
      persist(baseAreasRef.current.filter((a) => a.id !== id));
    },
    [persist],
  );

  const navigate = useNavigate();
  const { createSession } = useSession();

  const onMapStream = useCallback(
    async (area: StreamArea) => {
      setMappingErrors((prev) => {
        const next = { ...prev };
        delete next[area.id];
        return next;
      });

      if (area.mappingSessionId) {
        navigate(`/streams/${encodeURIComponent(area.id)}`);
        return;
      }

      if (!currentEngagementId) {
        setMappingErrors((prev) => ({
          ...prev,
          [area.id]: 'Choose an engagement before starting stream mapping.',
        }));
        return;
      }

      setMappingId(area.id);
      try {
        const sessionId = await createSession('discovery', {
          engagementId: currentEngagementId,
          streamId: area.id,
        });
        const next = baseAreasRef.current.map((candidate) =>
          candidate.id === area.id ? { ...candidate, mappingSessionId: sessionId } : candidate,
        );
        setAreas(next);
        await saveStreams({ streams: next });
        if (onChange) onChange();
        navigate(`/streams/${encodeURIComponent(area.id)}`);
      } catch (err) {
        setMappingErrors((prev) => ({
          ...prev,
          [area.id]: (err as Error).message || 'Could not start stream mapping.',
        }));
      } finally {
        setMappingId(null);
      }
    },
    [createSession, currentEngagementId, navigate, onChange, saveStreams],
  );

  return (
    <section className="sw-card" data-testid="scope-widget">
      <div className="sw-head">
        <div className="sw-head-row">
          <Building2 size={17} className="sw-head-icon" aria-hidden="true" />
          <span className="sw-title">Candidate value streams</span>
          <span className="sw-tag">Setup · 3 of 3</span>
          <span className="sw-head-note">sets what gets mapped</span>
          <Link to="/" className="sw-done">
            Done
          </Link>
        </div>
        <p className="sw-opener">
          What are the main candidate value streams this programme covers? Think how you&rsquo;d
          describe it to a new hire &mdash; <em>Claims, Underwriting, Finance, Onboarding&hellip;</em>{' '}
          Assign someone who knows each stream; each is the stream a later mapping loop attaches to.
        </p>
        <p className="sw-wedge">Start with one small stream — you can add more later.</p>
      </div>

      <div className="sw-rows">
        {displayAreas.map((area) => (
          <AreaRow
            key={area.id}
            area={area}
            isAssigning={assigningId === area.id}
            isEditing={editingId === area.id}
            isMapping={mappingId === area.id}
            mappingError={mappingErrors[area.id] ?? null}
            onAssignOpen={() => {
              setEditingId(null);
              setAssigningId(area.id);
            }}
            onAssignSave={(sme) => onAssignSave(area.id, sme)}
            onAssignCancel={() => setAssigningId(null)}
            onEditOpen={() => {
              setAssigningId(null);
              setEditingId(area.id);
            }}
            onEditSave={(name) => onEditSave(area.id, name)}
            onEditCancel={() => setEditingId(null)}
            onRemove={() => onRemove(area.id)}
            onMap={() => onMapStream(area)}
          />
        ))}

        <div className="sw-addrow">
          <PlusCircle size={18} className="sw-add-icon" aria-hidden="true" />
          <input
            ref={addRef}
            className="sw-field sw-add-field"
            type="text"
            aria-label="Add a value stream"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitAdd();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setDraftName('');
              }
            }}
            placeholder="Add a value stream — e.g. Claims handling"
          />
          <span className="sw-add-hint" aria-hidden="true">
            press Enter
          </span>
        </div>
      </div>
    </section>
  );
};

export default ScopeWidget;
